// FILE: src/pages/Attendance/AttendancePage.jsx
// Clock-in/out log, manual entry, active clock-ins
import React, { useEffect, useState, useCallback } from 'react';
import { attendanceAPI, clientsAPI, caregiversAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Attendance.css';

const fmtDT = (d) =>
  d ? new Date(d).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';
const toInputDT = (d) => d ? new Date(d).toISOString().slice(0,16) : '';

export default function AttendancePage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','CaseManager');

  const [records,    setRecords]    = useState([]);
  const [active,     setActive]     = useState([]);
  const [clients,    setClients]    = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [showManual, setShowManual] = useState(false);
  const [clockOutId, setClockOutId] = useState(null);
  const [coNotes,    setCoNotes]    = useState('');
  const [coLoad,     setCoLoad]     = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [aRes, actRes, cRes, cgRes] = await Promise.all([
        attendanceAPI.getAll(),
        attendanceAPI.getActive(),
        clientsAPI.getAll(),
        caregiversAPI.getAll(),
      ]);
      setRecords(aRes.data.data    || []);
      setActive(actRes.data.data   || []);
      setClients(cRes.data.data    || []);
      setCaregivers(cgRes.data.data|| []);
    } catch { toast.error('Failed to load attendance.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = records.filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.CaregiverName||'').toLowerCase().includes(q) ||
           (r.ClientName||'').toLowerCase().includes(q);
  });

  const handleClockOut = async () => {
    if (!clockOutId) return;
    setCoLoad(true);
    try {
      await attendanceAPI.clockOut(clockOutId, { Notes: coNotes });
      toast.success('Clocked out successfully.');
      setClockOutId(null);
      setCoNotes('');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Clock out failed.'); }
    finally        { setCoLoad(false); }
  };

  const totalHours = records.reduce((sum, r) => sum + parseFloat(r.TotalHours||0), 0);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Attendance</h1>
          <p>{records.length} records · {totalHours.toFixed(1)} total hours logged</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search caregiver or client…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:240}} />
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setShowManual(true)}>
              ➕ Manual Entry
            </button>
          )}
        </div>
      </div>

      {/* Active clock-ins */}
      {active.length > 0 && (
        <div className="card" style={{marginBottom:18,borderLeft:'4px solid var(--success)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <span style={{fontWeight:700,color:'var(--gray-800)'}}>
              🟢 Currently Clocked In ({active.length})
            </span>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
            {active.map(a => (
              <div key={a.ID} className="active-clock-card">
                <div style={{fontWeight:600,fontSize:'0.88rem'}}>{a.CaregiverName}</div>
                <div style={{fontSize:'0.75rem',color:'var(--gray-500)',marginTop:2}}>
                  {a.ClientName} · since {fmtDT(a.ClockInTime)}
                </div>
                {canEdit && (
                  <button className="btn btn-sm btn-danger" style={{marginTop:8,width:'100%'}}
                    onClick={() => { setClockOutId(a.ID); setCoNotes(''); }}>
                    ⏹ Clock Out
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Total Records',  value:records.length,                              icon:'📋'},
          {label:'Total Hours',    value:`${totalHours.toFixed(1)}h`,                 icon:'⏰'},
          {label:'Active Now',     value:active.length,                               icon:'🟢'},
          {label:'Avg Hours/Visit',value:records.length>0?(totalHours/records.length).toFixed(1)+'h':'—', icon:'📊'},
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header">
              <span className="stat-card-label">{s.label}</span>
              <span style={{fontSize:20}}>{s.icon}</span>
            </div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Records table */}
      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading attendance…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">⏰</div>
            <div className="empty-state-text">{search ? 'No records match.' : 'No attendance records yet.'}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr>
                  <th>Caregiver</th><th>Client</th><th>Clock In</th>
                  <th>Clock Out</th><th>Total Hours</th><th>GPS</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.ID}>
                    <td className="bold">{r.CaregiverName||'—'}</td>
                    <td style={{color:'var(--gray-600)',fontSize:'0.875rem'}}>{r.ClientName||'—'}</td>
                    <td style={{fontSize:'0.8rem'}}>{fmtDT(r.ClockInTime)}</td>
                    <td style={{fontSize:'0.8rem'}}>
                      {r.ClockOutTime
                        ? fmtDT(r.ClockOutTime)
                        : <span className="badge badge-warning">Active</span>
                      }
                    </td>
                    <td>
                      {r.TotalHours
                        ? <span style={{fontWeight:600,color:'var(--primary)'}}>{parseFloat(r.TotalHours).toFixed(2)}h</span>
                        : <span style={{color:'var(--gray-400)'}}>—</span>
                      }
                    </td>
                    <td style={{fontSize:'0.75rem',color:'var(--gray-400)'}}>
                      {r.ClockInLatitude
                        ? <span className="badge badge-success">✓ GPS</span>
                        : <span className="badge badge-gray">No GPS</span>
                      }
                    </td>
                    <td style={{fontSize:'0.78rem',color:'var(--gray-500)',maxWidth:180,
                      overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                      {r.Notes||'—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Clock-out confirm */}
      {clockOutId && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">⏹ Clock Out</span>
              <button className="modal-close" onClick={() => setClockOutId(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Visit Notes (optional)</label>
                <textarea className="form-control" rows={3}
                  value={coNotes} onChange={e => setCoNotes(e.target.value)}
                  placeholder="Add any notes about this visit…" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setClockOutId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleClockOut} disabled={coLoad}>
                {coLoad ? 'Clocking Out…' : '⏹ Confirm Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual entry modal */}
      {showManual && (
        <ManualEntryModal
          clients={clients} caregivers={caregivers}
          onClose={() => setShowManual(false)}
          onSaved={() => { setShowManual(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Manual Entry Modal ────────────────────────────────────
function ManualEntryModal({ clients, caregivers, onClose, onSaved }) {
  const [form,   setForm]   = useState({
    CaregiverID:'', ClientID:'', ClockInTime:'', ClockOutTime:'', Notes:'',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (f,v) => { setForm(p=>({...p,[f]:v})); if(errors[f]) setErrors(p=>({...p,[f]:''})); };

  const validate = () => {
    const e = {};
    if (!form.CaregiverID)  e.CaregiverID  = 'Required';
    if (!form.ClientID)     e.ClientID     = 'Required';
    if (!form.ClockInTime)  e.ClockInTime  = 'Required';
    if (!form.ClockOutTime) e.ClockOutTime = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await attendanceAPI.createManual(form);
      toast.success('Manual attendance record created.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed.'); }
    finally        { setSaving(false); }
  };

  const Err = ({f}) => errors[f] ? <div style={{color:'var(--danger)',fontSize:'0.75rem',marginTop:3}}>{errors[f]}</div> : null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">➕ Manual Attendance Entry</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Caregiver <span className="req">*</span></label>
              <select className={`form-control${errors.CaregiverID?' input-error':''}`}
                value={form.CaregiverID} onChange={e => set('CaregiverID',e.target.value)}>
                <option value="">— Select —</option>
                {caregivers.map(c=><option key={c.UserID} value={c.UserID}>{c.FirstName} {c.LastName}</option>)}
              </select>
              <Err f="CaregiverID"/>
            </div>
            <div className="form-group">
              <label className="form-label">Client <span className="req">*</span></label>
              <select className={`form-control${errors.ClientID?' input-error':''}`}
                value={form.ClientID} onChange={e => set('ClientID',e.target.value)}>
                <option value="">— Select —</option>
                {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
              </select>
              <Err f="ClientID"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Clock In <span className="req">*</span></label>
              <input className={`form-control${errors.ClockInTime?' input-error':''}`}
                type="datetime-local" value={form.ClockInTime} onChange={e=>set('ClockInTime',e.target.value)}/>
              <Err f="ClockInTime"/>
            </div>
            <div className="form-group">
              <label className="form-label">Clock Out <span className="req">*</span></label>
              <input className={`form-control${errors.ClockOutTime?' input-error':''}`}
                type="datetime-local" value={form.ClockOutTime} onChange={e=>set('ClockOutTime',e.target.value)}/>
              <Err f="ClockOutTime"/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.Notes}
              onChange={e=>set('Notes',e.target.value)} placeholder="Reason for manual entry…"/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '✅ Create Record'}
          </button>
        </div>
      </div>
    </div>
  );
}